import { FC, useCallback } from "react";
import { PageWrapper } from "@connectedComponents";
import { stackEnum } from "@utilities/enums";
import { useController } from "./useController";
import { Card, GenericActionsBar } from "@components";
import { CreditCard, ClosedCreditCard } from "./components";
import { formatPrice } from "@utilities/functions";

export const CreditsManagement: FC = () => {

    const {
        filteredCredits, filteredClosedCredits, setSearchQuery,
        totalRemainingAmount, totalClosedAmount,
        goBack, handleCreditPayment, creditPaymentLoading,
        tabs, activeTab, handleTabSwitch,
    } = useController();

    const renderActiveCredits = useCallback(() => (
        <>
            {/* Affichage du total des montants restants */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between">
                    <span className="text-red-700 font-medium">Total des montants restants dus :</span>
                    <span className="text-red-800 font-bold text-lg">{formatPrice(totalRemainingAmount)}</span>
                </div>
                <p className="text-red-600 text-sm mt-1">
                    {filteredCredits.length} crédit{filteredCredits.length > 1 ? 's' : ''} en cours
                </p>
            </div>
            {
                filteredCredits.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                        {
                            filteredCredits.map((credit, i) => (
                                <CreditCard
                                    key={i}
                                    credit={credit}
                                    onPayCredit={handleCreditPayment}
                                    isLoading={creditPaymentLoading}
                                />
                            ))
                        }
                    </div>
                ) : (
                    <div className="flex justify-center items-center h-full mt-4">
                        <p className="text-gray-500">Aucun crédit en cours.</p>
                    </div>
                )
            }
        </>
    ), [filteredCredits, totalRemainingAmount, handleCreditPayment, creditPaymentLoading]);

    const renderClosedCredits = useCallback(() => (
        <>
            {/* Affichage du total des crédits soldés */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between">
                    <span className="text-green-700 font-medium">Total des crédits soldés :</span>
                    <span className="text-green-800 font-bold text-lg">{formatPrice(totalClosedAmount)}</span>
                </div>
                <p className="text-green-600 text-sm mt-1">
                    {filteredClosedCredits.length} crédit{filteredClosedCredits.length > 1 ? 's' : ''} soldé{filteredClosedCredits.length > 1 ? 's' : ''}
                </p>
            </div>
            {
                filteredClosedCredits.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                        {
                            filteredClosedCredits.map((credit, i) => (
                                <ClosedCreditCard
                                    key={i}
                                    credit={credit}
                                />
                            ))
                        }
                    </div>
                ) : (
                    <div className="flex justify-center items-center h-full mt-4">
                        <p className="text-gray-500">Aucun crédit soldé.</p>
                    </div>
                )
            }
        </>
    ), [filteredClosedCredits, totalClosedAmount]);

    const renderTabContent = useCallback(() => {
        switch (activeTab) {
            case 0:
                return renderActiveCredits();
            case 1:
                return renderClosedCredits();
            default:
                return renderActiveCredits();
        }
    }, [activeTab, renderActiveCredits, renderClosedCredits]);

    return (
        <PageWrapper
            activeStack={stackEnum.stocksStack}
            handleGoBack={goBack}
        >
            <Card
                title="Gestions des crédits"
                actions={
                    <GenericActionsBar
                        searchPlaceholder="Rechercher un client, une commande..."
                        handleSearch={setSearchQuery}
                    />
                }
            >
                {/* Tabs */}
                <div
                    role="tablist"
                    className="tabs bg-white tabs-boxed mb-6 shadow overflow-x-auto h-14"
                >
                    {
                        tabs.map((tab, index) => (
                            <span
                                key={index}
                                role="tab"
                                className={`
                                    text-sm truncate whitespace-nowrap h-12
                                    tab ${activeTab === tab.index ? 'tab-active' : ''}
                                `}
                                onClick={() => handleTabSwitch(tab.index)}
                            >
                                {tab.label}
                            </span>
                        ))
                    }
                </div>

                {renderTabContent()}
            </Card>
        </PageWrapper>
    );
};
